/**
 * Admin Reddit workspace — credentials + posting + search via the
 * Worker-mediated ReddAPI integration. Lives inside the Creator Hub
 * sidebar as its own section so the admin can drive Reddit traffic
 * (per the memory note on the OnlyJalen Reddit funnel strategy)
 * without leaving the dashboard.
 *
 * Architecture
 * ────────────
 *   Accounts tab — manage saved Reddit accounts (creds encrypted at
 *                  rest by the Worker via `cryptoBox`)
 *   Compose tab  — pick an action (post-to-subreddit, post-to-profile,
 *                  reply, join), fill the fields, send
 *   Search tab   — subreddit search via reddapi (read-only)
 *   Activity tab — last 50 action-log rows
 *
 * All credentials live server-side. The browser only ever sees the
 * account's username/label/has-flags — never the raw bearer or cookies.
 */

import React, { useEffect, useState } from 'react';
import './RedditPanel.css';
import {
  redditAPI,
  RedditAccountSummary,
  RedditConfig,
  RedditSearchHit,
  RedditActionLogEntry,
} from '../../services/api';
import { toast } from '../../components/Toast/Toast';

type RedditTab = 'compose' | 'search' | 'accounts' | 'activity';
type ComposeMode = 'subreddit' | 'profile' | 'reply' | 'join';
type PostKind = 'text' | 'image' | 'video';

const RedditPanel: React.FC = () => {
  const [tab, setTab] = useState<RedditTab>('compose');
  const [accounts, setAccounts] = useState<RedditAccountSummary[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [config, setConfig] = useState<RedditConfig | null>(null);

  // Compose state
  const [composeMode, setComposeMode] = useState<ComposeMode>('subreddit');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [subreddit, setSubreddit] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isNsfw, setIsNsfw] = useState(false);
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [flairText, setFlairText] = useState('');
  const [flairId, setFlairId] = useState('');
  const [postKind, setPostKind] = useState<PostKind>('text');
  const [mediaUrl, setMediaUrl] = useState('');
  const [commentUrl, setCommentUrl] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Search state
  const [searchSubreddit, setSearchSubreddit] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSort, setSearchSort] = useState<'new' | 'hot' | 'top' | 'relevance' | 'comments'>('new');
  const [searchTime, setSearchTime] = useState<'hour' | 'day' | 'week' | 'month' | 'year' | 'all'>('day');
  const [searchResults, setSearchResults] = useState<RedditSearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  // New account form
  const [addOpen, setAddOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newBearer, setNewBearer] = useState('');
  const [newCookiesJson, setNewCookiesJson] = useState('');
  const [newProxy, setNewProxy] = useState('');
  const [creating, setCreating] = useState(false);

  // Activity log
  const [activity, setActivity] = useState<RedditActionLogEntry[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const refreshAccounts = async () => {
    setAccountsLoading(true);
    try {
      const r = await redditAPI.listAccounts();
      setAccounts(r.accounts || []);
      if (r.accounts.length > 0 && !selectedAccountId) {
        setSelectedAccountId(r.accounts[0].id);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not load accounts');
    } finally {
      setAccountsLoading(false);
    }
  };

  const refreshConfig = async () => {
    try {
      const r = await redditAPI.config();
      setConfig({ rapidapi_key_set: r.rapidapi_key_set, cred_secret_set: r.cred_secret_set });
    } catch {
      // Non-fatal — banner just stays hidden.
    }
  };

  const refreshActivity = async () => {
    setActivityLoading(true);
    try {
      const r = await redditAPI.log();
      setActivity(r.log || []);
    } catch (e: any) {
      toast.error(e?.message || 'Could not load activity');
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    refreshConfig();
    refreshAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'activity') refreshActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleCreateAccount = async () => {
    if (!newUsername.trim()) {
      toast.error('Username is required');
      return;
    }
    let cookiesPayload: Record<string, string> | string | undefined;
    if (newCookiesJson.trim()) {
      try {
        cookiesPayload = JSON.parse(newCookiesJson);
      } catch {
        // Fall through — backend tolerates a string too if it's not parseable JSON
        cookiesPayload = newCookiesJson.trim();
      }
    }
    setCreating(true);
    try {
      const r = await redditAPI.createAccount({
        username: newUsername.trim(),
        label: newLabel.trim() || undefined,
        bearer_token: newBearer.trim() || undefined,
        cookies: cookiesPayload,
        proxy: newProxy.trim() || undefined,
      });
      if (r.success) {
        toast.success(`Account u/${r.account.username} saved`);
        setAddOpen(false);
        setNewUsername(''); setNewLabel(''); setNewBearer(''); setNewCookiesJson(''); setNewProxy('');
        await refreshAccounts();
      } else {
        toast.error(r.message || 'Could not save account');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not save account');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAccount = async (acct: RedditAccountSummary) => {
    if (!window.confirm(`Remove u/${acct.username}? Stored credentials will be permanently deleted.`)) return;
    try {
      await redditAPI.deleteAccount(acct.id);
      toast.success('Account removed');
      if (selectedAccountId === acct.id) setSelectedAccountId('');
      await refreshAccounts();
    } catch (e: any) {
      toast.error(e?.message || 'Could not remove account');
    }
  };

  const handleSearch = async () => {
    if (!searchSubreddit.trim() || !searchQuery.trim()) {
      toast.error('Subreddit and query are required');
      return;
    }
    setSearching(true);
    setSearchResults(null);
    try {
      const r = await redditAPI.searchSubreddit({
        subreddit: searchSubreddit.trim(),
        query: searchQuery.trim(),
        sort: searchSort,
        time_filter: searchTime,
        limit: 50,
      });
      if (r.success && Array.isArray(r.data)) {
        setSearchResults(r.data);
        if (r.data.length === 0) toast.info?.('No matches');
      } else {
        toast.error(r.message || 'Search failed');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSend = async () => {
    if (!selectedAccountId) {
      toast.error('Pick an account first');
      return;
    }
    setSending(true);
    try {
      let r;
      if (composeMode === 'subreddit') {
        const needsMedia = postKind !== 'text';
        if (!subreddit.trim() || !title.trim()) {
          toast.error('Subreddit and title are required');
          setSending(false);
          return;
        }
        if (needsMedia && !mediaUrl.trim()) {
          toast.error(`${postKind === 'image' ? 'Image' : 'Video'} URL is required`);
          setSending(false);
          return;
        }
        if (!needsMedia && !body.trim()) {
          toast.error('Body is required for text posts');
          setSending(false);
          return;
        }
        r = await redditAPI.postToSubreddit({
          account_id: selectedAccountId,
          subreddit: subreddit.trim(),
          title: title.trim(),
          text: needsMedia ? (body || undefined) : body,
          image_url: postKind === 'image' ? mediaUrl.trim() : undefined,
          video_url: postKind === 'video' ? mediaUrl.trim() : undefined,
          is_nsfw: isNsfw,
          is_spoiler: isSpoiler,
          flair_id: flairId.trim() || undefined,
          flair_text: flairText.trim() || undefined,
        });
      } else if (composeMode === 'profile') {
        const needsMedia = postKind !== 'text';
        if (!title.trim()) {
          toast.error('Title is required');
          setSending(false);
          return;
        }
        if (needsMedia && !mediaUrl.trim()) {
          toast.error(`${postKind === 'image' ? 'Image' : 'Video'} URL is required`);
          setSending(false);
          return;
        }
        if (!needsMedia && !body.trim()) {
          toast.error('Body is required for text posts');
          setSending(false);
          return;
        }
        r = await redditAPI.postToProfile({
          account_id: selectedAccountId,
          title: title.trim(),
          text: needsMedia ? (body || undefined) : body,
          image_url: postKind === 'image' ? mediaUrl.trim() : undefined,
          video_url: postKind === 'video' ? mediaUrl.trim() : undefined,
          is_nsfw: isNsfw,
        });
      } else if (composeMode === 'reply') {
        if (!commentUrl.trim() || !replyText.trim()) {
          toast.error('Comment URL and reply are required');
          setSending(false);
          return;
        }
        r = await redditAPI.replyComment({
          account_id: selectedAccountId,
          comment_url: commentUrl.trim(),
          reply: replyText,
        });
      } else {
        if (!subreddit.trim()) {
          toast.error('Subreddit is required');
          setSending(false);
          return;
        }
        r = await redditAPI.joinSubreddit({
          account_id: selectedAccountId,
          subreddit: subreddit.trim(),
        });
      }
      if (r?.success) {
        const url = (r as any)?.data?.url || (r as any)?.data?.reply_url;
        toast.success(url ? `Sent — ${url}` : (r.message || 'Sent'));
        // Soft reset of the body fields so the operator doesn't post the same thing twice
        if (composeMode === 'subreddit' || composeMode === 'profile') {
          setTitle(''); setBody(''); setMediaUrl('');
        } else if (composeMode === 'reply') {
          setCommentUrl(''); setReplyText('');
        }
      } else {
        toast.error(r?.message || 'Reddit refused the request');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Request failed');
    } finally {
      setSending(false);
    }
  };

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  return (
    <div className="section-container reddit-section">
      <div className="section-header">
        <div>
          <h2>Reddit Funnel</h2>
          <p className="section-subtitle">
            Drive subscribers from Reddit · {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'} configured
          </p>
        </div>
      </div>

      {/* Config / secrets banner — surfaces missing worker secrets so
          the admin doesn't smash buttons that 500 silently. */}
      {config && (!config.rapidapi_key_set || !config.cred_secret_set) && (
        <div className="reddit-banner reddit-banner--warn">
          <strong>Setup incomplete.</strong>
          {!config.rapidapi_key_set && (
            <span> Set the RapidAPI key: <code>wrangler secret put RAPIDAPI_KEY</code>.</span>
          )}
          {!config.cred_secret_set && (
            <span> Set the cred-encryption key: <code>wrangler secret put REDDIT_CRED_SECRET</code>.</span>
          )}
          <span className="reddit-banner-help"> Both must be set on the <code>onlyjalen-api</code> worker.</span>
        </div>
      )}

      <div className="reddit-tabs" role="tablist">
        {([
          { id: 'compose',  label: 'Compose',  icon: '✏️' },
          { id: 'search',   label: 'Search',   icon: '🔎' },
          { id: 'accounts', label: 'Accounts', icon: '🔑' },
          { id: 'activity', label: 'Activity', icon: '📜' },
        ] as const).map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`reddit-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="reddit-tab-icon" aria-hidden="true">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ────────────── COMPOSE ────────────── */}
      {tab === 'compose' && (
        <div className="reddit-compose">
          <div className="reddit-row">
            <label className="reddit-field">
              <span>Account</span>
              <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}>
                <option value="">— Pick a Reddit account —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>
                    u/{a.username}{a.label ? ` · ${a.label}` : ''}
                  </option>
                ))}
              </select>
              {selectedAccount && (
                <span className="reddit-account-caps">
                  {selectedAccount.has_bearer ? '🔓 bearer' : ''}
                  {selectedAccount.has_cookies ? ' 🍪 cookies' : ''}
                  {selectedAccount.has_proxy ? ' 🌐 proxy' : ''}
                </span>
              )}
            </label>
            <label className="reddit-field">
              <span>Action</span>
              <select value={composeMode} onChange={e => setComposeMode(e.target.value as ComposeMode)}>
                <option value="subreddit">Post to subreddit</option>
                <option value="profile">Post to my profile</option>
                <option value="reply">Reply to comment</option>
                <option value="join">Join subreddit</option>
              </select>
            </label>
          </div>

          {(composeMode === 'subreddit' || composeMode === 'join') && (
            <label className="reddit-field">
              <span>Subreddit</span>
              <input
                type="text"
                placeholder="askreddit"
                value={subreddit}
                onChange={e => setSubreddit(e.target.value.replace(/^\/?r\//, ''))}
              />
            </label>
          )}

          {(composeMode === 'subreddit' || composeMode === 'profile') && (
            <>
              <label className="reddit-field">
                <span>Title <span className="reddit-counter">{title.length}/300</span></span>
                <input
                  type="text"
                  maxLength={300}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Catch attention — first 60 chars show in feeds"
                />
              </label>
              <label className="reddit-field">
                <span>Post type</span>
                <select value={postKind} onChange={e => setPostKind(e.target.value as PostKind)}>
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </label>
              {postKind !== 'text' && (
                <label className="reddit-field">
                  <span>{postKind === 'image' ? 'Image URL' : 'Video URL'}</span>
                  <input
                    type="url"
                    value={mediaUrl}
                    onChange={e => setMediaUrl(e.target.value)}
                    placeholder={postKind === 'image'
                      ? 'https://i.redd.it/... or https://i.imgur.com/...jpg'
                      : 'https://v.redd.it/... or https://youtu.be/...'}
                  />
                  <span className="reddit-counter">
                    {postKind === 'image'
                      ? 'Paste a direct image URL (.jpg/.png/.gif). i.redd.it and i.imgur.com both work.'
                      : 'Paste a direct video URL or YouTube link. v.redd.it is most reliable.'}
                  </span>
                </label>
              )}
              <label className="reddit-field">
                <span>{postKind === 'text' ? 'Body' : 'Caption (optional)'}</span>
                <textarea
                  rows={postKind === 'text' ? 8 : 4}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder={postKind === 'text'
                    ? 'Reddit accepts Markdown. Blank lines = paragraph breaks.'
                    : 'Optional caption shown under the media.'}
                />
              </label>
              <div className="reddit-row">
                <label className="reddit-check">
                  <input type="checkbox" checked={isNsfw} onChange={e => setIsNsfw(e.target.checked)} />
                  <span>Mark NSFW</span>
                </label>
                {composeMode === 'subreddit' && (
                  <label className="reddit-check">
                    <input type="checkbox" checked={isSpoiler} onChange={e => setIsSpoiler(e.target.checked)} />
                    <span>Mark spoiler</span>
                  </label>
                )}
              </div>
              {composeMode === 'subreddit' && (
                <div className="reddit-row">
                  <label className="reddit-field">
                    <span>Flair text (optional)</span>
                    <input type="text" value={flairText} onChange={e => setFlairText(e.target.value)} />
                  </label>
                  <label className="reddit-field">
                    <span>Flair ID (optional)</span>
                    <input type="text" value={flairId} onChange={e => setFlairId(e.target.value)} />
                  </label>
                </div>
              )}
            </>
          )}

          {composeMode === 'reply' && (
            <>
              <label className="reddit-field">
                <span>Comment URL</span>
                <input
                  type="url"
                  value={commentUrl}
                  onChange={e => setCommentUrl(e.target.value)}
                  placeholder="https://www.reddit.com/r/.../comment/abc123/"
                />
              </label>
              <label className="reddit-field">
                <span>Reply <span className="reddit-counter">{replyText.length}/10000</span></span>
                <textarea
                  rows={6}
                  maxLength={10000}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Markdown is allowed."
                />
              </label>
            </>
          )}

          <div className="reddit-actions">
            <button
              type="button"
              className="reddit-send"
              onClick={handleSend}
              disabled={sending || !selectedAccountId}
            >
              {sending ? 'Sending…' : composeMode === 'join'
                ? `Join r/${subreddit || '…'}`
                : composeMode === 'reply'
                  ? 'Send reply'
                  : composeMode === 'profile'
                    ? 'Post to my profile'
                    : `Post to r/${subreddit || '…'}`}
            </button>
          </div>
        </div>
      )}

      {/* ────────────── SEARCH ────────────── */}
      {tab === 'search' && (
        <div className="reddit-search">
          <div className="reddit-row">
            <label className="reddit-field">
              <span>Subreddit</span>
              <input
                type="text"
                value={searchSubreddit}
                onChange={e => setSearchSubreddit(e.target.value.replace(/^\/?r\//, ''))}
                placeholder="askreddit"
              />
            </label>
            <label className="reddit-field reddit-field--wide">
              <span>Query</span>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="how to gain muscle"
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              />
            </label>
          </div>
          <div className="reddit-row">
            <label className="reddit-field">
              <span>Sort</span>
              <select value={searchSort} onChange={e => setSearchSort(e.target.value as any)}>
                <option value="new">New</option>
                <option value="hot">Hot</option>
                <option value="top">Top</option>
                <option value="relevance">Relevance</option>
                <option value="comments">Most comments</option>
              </select>
            </label>
            <label className="reddit-field">
              <span>Time window</span>
              <select value={searchTime} onChange={e => setSearchTime(e.target.value as any)}>
                <option value="hour">Past hour</option>
                <option value="day">Past day</option>
                <option value="week">Past week</option>
                <option value="month">Past month</option>
                <option value="year">Past year</option>
                <option value="all">All time</option>
              </select>
            </label>
            <div className="reddit-actions">
              <button type="button" className="reddit-send" onClick={handleSearch} disabled={searching}>
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>

          {searchResults && (
            <ul className="reddit-results">
              {searchResults.length === 0 ? (
                <li className="reddit-results-empty">No matches</li>
              ) : (
                searchResults.map(hit => (
                  <li key={hit.id} className="reddit-result">
                    <a href={hit.url} target="_blank" rel="noopener noreferrer" className="reddit-result-title">
                      {hit.title}
                    </a>
                    <div className="reddit-result-meta">
                      r/{hit.subreddit} · u/{hit.author} · {hit.score} pts · {hit.num_comments} comments
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}

      {/* ────────────── ACCOUNTS ────────────── */}
      {tab === 'accounts' && (
        <div className="reddit-accounts">
          <div className="reddit-accounts-header">
            <span className="reddit-accounts-count">
              {accountsLoading ? 'Loading…' : `${accounts.length} ${accounts.length === 1 ? 'account' : 'accounts'}`}
            </span>
            <button type="button" className="reddit-send" onClick={() => setAddOpen(true)}>+ Add account</button>
          </div>

          {addOpen && (
            <div className="reddit-add-form">
              <h4>Add Reddit account</h4>
              <p className="reddit-add-help">
                Bearer is required for profile posts. Cookies are required for subreddit posts, replies, and joins.
                Proxy is optional but recommended. All fields are encrypted at rest on the Worker.
              </p>
              <label className="reddit-field">
                <span>Reddit username</span>
                <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="onlyjalen" />
              </label>
              <label className="reddit-field">
                <span>Label (optional)</span>
                <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="burner #1" />
              </label>
              <label className="reddit-field">
                <span>Bearer token (optional)</span>
                <input type="password" value={newBearer} onChange={e => setNewBearer(e.target.value)} placeholder="paste bearer" />
              </label>
              <label className="reddit-field">
                <span>Cookies JSON (optional)</span>
                <textarea
                  rows={4}
                  value={newCookiesJson}
                  onChange={e => setNewCookiesJson(e.target.value)}
                  placeholder='{"reddit_session": "..."}'
                />
              </label>
              <label className="reddit-field">
                <span>Proxy (optional)</span>
                <input
                  type="text"
                  value={newProxy}
                  onChange={e => setNewProxy(e.target.value)}
                  placeholder="user:pass@host:port"
                />
              </label>
              <div className="reddit-actions reddit-actions--end">
                <button type="button" className="reddit-cancel" onClick={() => setAddOpen(false)} disabled={creating}>Cancel</button>
                <button type="button" className="reddit-send" onClick={handleCreateAccount} disabled={creating}>
                  {creating ? 'Saving…' : 'Save account'}
                </button>
              </div>
            </div>
          )}

          {accountsLoading ? (
            <div className="reddit-loading">Loading accounts…</div>
          ) : accounts.length === 0 ? (
            <div className="reddit-empty">
              <p>No Reddit accounts yet.</p>
              <span>Add one to start posting, replying, or joining subreddits.</span>
            </div>
          ) : (
            <ul className="reddit-accounts-list">
              {accounts.map(a => (
                <li key={a.id} className="reddit-account-card">
                  <div className="reddit-account-name">
                    u/{a.username}
                    {a.label && <span className="reddit-account-label">{a.label}</span>}
                  </div>
                  <div className="reddit-account-caps">
                    {a.has_bearer ? <span className="cap cap--on">bearer</span> : <span className="cap cap--off">no bearer</span>}
                    {a.has_cookies ? <span className="cap cap--on">cookies</span> : <span className="cap cap--off">no cookies</span>}
                    {a.has_proxy ? <span className="cap cap--on">proxy</span> : <span className="cap cap--off">no proxy</span>}
                  </div>
                  <button type="button" className="reddit-account-remove" onClick={() => handleDeleteAccount(a)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ────────────── ACTIVITY ────────────── */}
      {tab === 'activity' && (
        <div className="reddit-activity">
          {activityLoading && !activity ? (
            <div className="reddit-loading">Loading log…</div>
          ) : !activity || activity.length === 0 ? (
            <div className="reddit-empty">
              <p>No Reddit activity yet.</p>
              <span>Every search, post, reply, and join is logged here for audit.</span>
            </div>
          ) : (
            <ul className="reddit-log">
              {activity.map(row => (
                <li key={row.id} className={`reddit-log-row ${row.success ? 'ok' : 'fail'}`}>
                  <div className="reddit-log-top">
                    <span className="reddit-log-type">{row.action_type.replace('_', ' ')}</span>
                    <span className="reddit-log-target">{row.target || '—'}</span>
                    <span className="reddit-log-time">{new Date(row.created_at).toLocaleString()}</span>
                  </div>
                  <div className="reddit-log-bottom">
                    {row.success ? '✓' : '✕'} {row.result_summary || '—'}
                    {row.reddapi_status !== null && row.reddapi_status !== undefined && (
                      <span className="reddit-log-status"> · HTTP {row.reddapi_status}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default RedditPanel;
